import type { Insurance } from "@/types/insurance/insurance";
import { toFixedNumber } from "../utils";
import { getCompensationTaperStartAge } from "./taper-utils";

// Lovpålagt yrkesskade
export function legalMedicalDisabilityCompensation(
	age: number,
	invalidity: number,
	minimumInvalidity: number = 0.15
): number {
	if (typeof age !== "number" || Number.isNaN(age)) {
		console.error("legalMedicalDisabilityCompensation: age is not a valid number", {
			age,
		});
		return 0;
	}

	if (age < 20) {
		console.error(
			"legalMedicalDisabilityCompensation: age is below minimum of 20 years",
			{ age },
		);
		return 0;
	}

	if (!invalidity || invalidity < minimumInvalidity || invalidity > 1) {
		console.error("Invalidity is out of range");
		return 0;
	}

	const INCREASE = 0.02;
	const REDUCTION = 0.02;
	const MIN_COMPENSATION_PERCENT = 0.5;

	let sum: number = 0;
	age = Math.floor(age);

	const baseCoverageCases = [
		{ min: 0.15, max: 0.24, value: 0.75 },
		{ min: 0.25, max: 0.34, value: 1 },
		{ min: 0.35, max: 0.44, value: 1.5 },
		{ min: 0.45, max: 0.54, value: 2 },
		{ min: 0.55, max: 0.64, value: 2.5 },
		{ min: 0.65, max: 0.74, value: 3 },
		{ min: 0.75, max: 0.84, value: 3.75 },
		{ min: 0.85, max: 1, value: 4.5 },
	];

	let baseCoverage: number = 0;

	// For invalidity below 15%, use formula: 4.5 * invalidity
	if (invalidity < 0.15) {
		baseCoverage = 4.5 * invalidity;
	} else {
		// For invalidity >= 15%, use the lookup table
		const coverageCase = baseCoverageCases.find(
			(item) => invalidity >= item.min && invalidity <= item.max
		);
		if (coverageCase) {
			baseCoverage = coverageCase.value;
		}
	}

	if (baseCoverage === 0) {
		return 0;
	}

	if (age < 0) {
		return 0;
	}

	if (age === 45 || age === 46) {
		// Dersom personen er 45 eller 46 år
		sum = baseCoverage;
	}

	// Dersom personen er yngre 45 år
	if (age < 45) {
		sum = baseCoverage + baseCoverage * INCREASE * (45 - age);
	}

	// Dersom personen er eldre enn 46 år
	if (age > 46) {
		sum = baseCoverage + baseCoverage * REDUCTION * (46 - age);
	}

	const minCompensation = MIN_COMPENSATION_PERCENT * baseCoverage;

	// Dersom summen er mindre enn minimum erstatning
	if (sum < minCompensation) return toFixedNumber(minCompensation);

	return toFixedNumber(sum);
}

// Utvidet yrkesskade
export function additionalMedicalDisabilityCompensation(
	insurance: Insurance | undefined,
	age: number,
	invalidity: number,
	minimumInvalidity: number = 0.01
): number {
	let baseCoverageParam: number;
	let reduction: number;
	let increase: number;
	let minAge: number;
	let maxAge: number;

	try {
		baseCoverageParam = parseFloat(
			insurance?.params?.find((param) => param.type === "base-compensation")
				?.value as string
		);
		reduction =
			parseFloat(
				insurance?.params?.find((param) => param.type === "reduction")
					?.value as string
			) / 100;
		increase =
			parseFloat(
				insurance?.params?.find((param) => param.type === "increase")
					?.value as string
			) / 100;
		minAge = parseFloat(
			insurance?.params?.find((param) => param.type === "min-age")
				?.value as string
		);
		maxAge = parseFloat(
			insurance?.params?.find((param) => param.type === "max-age")
				?.value as string
		);
	} catch (error) {
		console.error("Error in additionalMedicalDisabilityCompensation:", error);
		return 0;
	}

	if (!baseCoverageParam || baseCoverageParam < 0) {
		return 0;
	}

	if (!minAge) {
		minAge = 0;
	}

	if (!maxAge) {
		maxAge = 72;
	}

	if (!age || age < minAge || age >= maxAge) {
		return 0;
	}

	if (!invalidity || invalidity < minimumInvalidity || invalidity > 1) {
		return 0;
	}

	if (minimumInvalidity < 0.01 || minimumInvalidity > 1) {
		console.error("Minimum invalidity is out of range");
		return 0;
	}

	if (invalidity < minimumInvalidity) {
		console.error("Invalidity is lower than minimum");
		return 0;
	}

	let baseCoverage = 0;

	// For invalidity below 15%, compensation is linear
	if (invalidity < 0.15) {
		baseCoverage = baseCoverageParam * invalidity;
	} else if (invalidity >= 0.15 && invalidity <= 1) {
		// For invalidity 15% and above, use the bracket system
		const baseCoverageCases = [
			{ min: 0.15, max: 0.24, value: (baseCoverageParam * 1) / 6 }, // 0.75
			{ min: 0.25, max: 0.34, value: (baseCoverageParam * 2) / 9 }, // 1
			{ min: 0.35, max: 0.44, value: (baseCoverageParam * 1) / 3 }, // 1.5
			{ min: 0.45, max: 0.54, value: (baseCoverageParam * 4) / 9 }, // 2
			{ min: 0.55, max: 0.64, value: (baseCoverageParam * 5) / 9 }, // 2.5
			{ min: 0.65, max: 0.74, value: (baseCoverageParam * 2) / 3 }, // 3 - FIXED: was 0.84
			{ min: 0.75, max: 0.84, value: (baseCoverageParam * 5) / 6 }, // 3.75
			{ min: 0.85, max: 1, value: baseCoverageParam }, // 4.5
		];
		
		baseCoverageCases.forEach((item) => {
			if (invalidity >= item.min && invalidity <= item.max) {
				baseCoverage = item.value;
			}
		});
	}

	if (baseCoverage === 0) {
		return 0;
	}

	let sum: number = 0;
	const minCompensationPercent = 0.5;
	age = Math.floor(age);

	const taperStartAge = getCompensationTaperStartAge(insurance);
	const plateauLowAge = taperStartAge - 1;
	const plateauHighAge = taperStartAge;

	if (age === plateauLowAge || age === plateauHighAge) {
		sum = baseCoverage;
	}

	if (age < plateauLowAge) {
		if (increase && increase > 0) {
			sum = baseCoverage + baseCoverage * increase * (plateauLowAge - age);
		} else {
			sum = baseCoverage;
		}
	}

	if (age > plateauHighAge) {
		if (reduction && reduction > 0) {
			sum =
				baseCoverage +
				baseCoverage * reduction * (plateauHighAge - age);
		} else {
			sum = baseCoverage;
		}
	}

	const minCoverage = minCompensationPercent * baseCoverage;

	if (sum < minCoverage) {
		return toFixedNumber(minCoverage);
	}

	return toFixedNumber(sum);
}
